import { MpxResponseMeta, TaskDetails } from '@cibo/core'
import { useBatchedFields } from '@cibo/landmanager'
import { BulkEditHeader } from '@cibo/landmanager/src/features/DetailEditing/EditMultipleFields/BulkEditHeader'
import { AutoFields, CiboAutoForm, ResponsiveDialog, SubmitField } from '@cibo/ui'
import AutoField from '@cibo/ui/src/components/Form/Uniforms/AutoField'
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { equals, pick } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../../context'
import { applyReplacerToSchema, createYearTokenContext } from '../schemaTokenContext'
import { CiboAutoFormDebugger } from './CiboAutoFormDebugger'
import { componentDetector } from './componentDetector'
import { DataEntrySchemaEditingState } from './types'

export const SchemaTraitDialog = ({
  state,
  setState,
  saveTaskData,
}: {
  state: DataEntrySchemaEditingState
  setState(newState: { open: boolean }): void
  saveTaskData(updates: any): Promise<(MpxResponseMeta & TaskDetails) | void>
}) => {
  const { t } = useTranslation('@cibo/landmanager/SchemaTraitDialog')

  const [model, setModel] = useState<{ value: any }>()

  const { taskData, taskStatus, engagement } = useProgramEngagementContext()
  const taskName = taskStatus?.displays?.find(display => display.type === 'menuItem')?.params.title

  const detail =
    state.traitConfig &&
    state.detailRequirement &&
    taskData.data?.getDetail({
      traitConfig: state.traitConfig,
      resourceId: state.resourceIds?.[0],
      detailRequirement: state.detailRequirement,
    })

  useEffect(() => {
    if (detail && state.traitConfig?.detailValueKey) {
      setModel({ value: detail[state.traitConfig.detailValueKey] })
    }
  }, [state.traitConfig?.detailValueKey, detail])

  useEffect(() => {
    !state.open && setModel(undefined)
  }, [state.open])

  const fieldModels = useBatchedFields(state.resourceIds || [])

  const traitSchema = state.traitConfig?.schema
  const schema = useMemo(
    () =>
      traitSchema && engagement.data
        ? applyReplacerToSchema(traitSchema, s => engagement.data.replaceCopyTokens(s))
        : traitSchema,
    [traitSchema, engagement.data]
  )

  // When editing multiple fields, the submit should only be disabled if every
  // selected field already has the same value as the current form model.
  const allFieldsMatchModel = useMemo(() => {
    const { resourceIds, traitConfig, detailRequirement } = state
    if (
      !resourceIds ||
      resourceIds.length <= 1 ||
      !traitConfig?.detailValueKey ||
      !detailRequirement ||
      !taskData.data
    ) {
      return undefined
    }
    const data = taskData.data
    const valueKey = traitConfig.detailValueKey as string
    return resourceIds.every(resourceId => {
      const fieldDetail = data.getDetail({ traitConfig, resourceId, detailRequirement })
      return equals((fieldDetail as Record<string, unknown>)?.[valueKey], model?.value)
    })
  }, [state.resourceIds, state.traitConfig, state.detailRequirement, taskData.data, model?.value])

  const handleSubmit = useCallback(
    (formModel: any) => {
      if (!state.resourceIds || !state.detailRequirement) return

      const baseDetail = pick(['traitId', 'year', 'programId'], state.detailRequirement)

      const detailValueKey = state.traitConfig?.detailValueKey

      const updates = state.resourceIds.map(resourceId => ({
        resourceId,
        details: [
          {
            ...baseDetail,
            [detailValueKey as string]: formModel.value,
          },
        ],
      }))

      return saveTaskData({
        fieldDetails: updates,
      })
        .then(() => {
          state.onSuccess?.(formModel.value)
        })
        .catch(() => {})
    },
    [saveTaskData, state.resourceIds, state.detailRequirement]
  )

  if (!state.traitConfig?.schema) {
    return null
  }

  const uiParams = state.traitConfig.schema['ui-params']

  if (!taskData.data || !state.detailRequirement) {
    return null
  }

  return (
    <ResponsiveDialog
      open={state.open}
      onClose={() => setState({ open: false })}
      data-testid="schema-trait-dialog"
    >
      <AutoField.componentDetectorContext.Provider value={componentDetector}>
        <CiboAutoForm
          schema={schema}
          model={model}
          onSubmit={handleSubmit}
          onChangeModel={setModel}
          tokenContext={createYearTokenContext(state.detailRequirement.year)}
          validate="onChange"
          warningSchema={
            uiParams?.warningSchema && {
              type: 'object',
              properties: { value: uiParams?.warningSchema },
            }
          }
        >
          <DialogTitle>
            {t('editSelectedFields', {
              traitName: taskName,
              count: fieldModels.data?.length,
            })}
          </DialogTitle>

          <DialogContent>
            <BulkEditHeader fieldModels={fieldModels.data} />

            <AutoFields />
          </DialogContent>

          <DialogActions data-testid="dialog-actions">
            <Button variant="outlined" onClick={() => setState({ open: false })}>
              {t('cancel')}
            </Button>

            <SubmitField disabled={allFieldsMatchModel === false ? false : undefined} />
          </DialogActions>

          <CiboAutoFormDebugger />
        </CiboAutoForm>
      </AutoField.componentDetectorContext.Provider>
    </ResponsiveDialog>
  )
}
